import * as React from 'react';
import { Input, Button, notify, Page, Card } from 'sha-el-design';
import { Inject } from '../../../helpers';
import { QuestionService } from '../../../service/questionService';
import { MdHome, MdArrowBack } from 'react-icons/md';
import { Link } from 'react-router-dom';
import '../../Common/listPageStyles';

/**
 * Add / Edit Question. EditQuestion renders this same component with the
 * fetched question as props.
 *
 * The old form kept `options` in two shapes at once: schema-initialized
 * `{option: ''}` objects and, after any keystroke, plain strings. The value
 * prop then read `.option` off a string (undefined), so the inputs silently
 * went uncontrolled -- and submit `.join(',')`-ed whatever was left, which
 * stringified untouched objects to '[object Object]'. Production question 28
 * ('Any Suggetion', TEXT) still carries options=['[object Object]'] from
 * exactly that path. Options are now plain strings from start to finish.
 */
export class AddQuestion extends React.Component<Props, State> {
  @Inject questionService: QuestionService;

  static TYPES = [
    { type: 'MULTIPLE CHOICE', blurb: 'The caller can pick several of the options.' },
    { type: 'SINGLE CHOICE', blurb: 'The caller picks exactly one option.' },
    { type: 'TEXT', blurb: 'The caller types a free-form answer.' },
  ];

  // The completion-email report indexes a fixed 4-slot counter per question
  // (views.py options_count) and hard-crashes on a fifth option, so the
  // client refuses to create what the backend cannot report on.
  static MAX_OPTIONS = 4;

  constructor(props: Props) {
    super(props);

    this.state = {
      question: this.normalize(this.props.question),
      lockSubmit: false,
    };
  }

  // graphene returns the CharField-with-choices as an enum name
  // ('MULTIPLE_CHOICE'); the model and the form expect the stored value
  // ('MULTIPLE CHOICE'). Normalize once, here, instead of the old
  // setState-in-constructor init().
  normalize = (q: Props['question']): QuestionFormValue => (q ? {
    id: q.id,
    name: q.name || '',
    description: q.description || '',
    questionType: (q.questionType || '').split('_').join(' '),
    // A TEXT question's options are NULL in the database; the old edit form
    // crashed on `null.join(',')` when submitting one.
    options: (q.options && q.options.length ? q.options.slice() : ['']),
  } : {
    name: '',
    description: '',
    questionType: '',
    options: [''],
  })

  isEdit = () => !!(this.props.question && this.props.question.id);

  // Referenced by a questionnaire: editing the wording or options would
  // silently change the meaning of answers already recorded against it.
  isLocked = () => !!(this.props.question && this.props.question.usedInQuestionnaire);

  onChange = (name: keyof QuestionFormValue, value: string) => {
    if (this.isLocked()) { return; }
    this.setState({ question: { ...this.state.question, [name]: value } });
  }

  optionChange = (value: string, index: number) => {
    if (this.isLocked()) { return; }
    const options = this.state.question.options.slice();
    // The transport joins options on ',' and the server splits on it again, so
    // a comma inside an option silently becomes two options. Refuse the key.
    options[index] = value.split(',').join('');
    this.setState({ question: { ...this.state.question, options } });
  }

  addOption = () => {
    const options = this.state.question.options.slice();
    if (this.isLocked() || options.length >= AddQuestion.MAX_OPTIONS) { return; }
    options.push('');
    this.setState({ question: { ...this.state.question, options } });
  }

  removeOption = (index: number) => {
    const options = this.state.question.options.slice();
    // The old handler refused to remove index 0 specifically; any row can go,
    // as long as one input is left to type into.
    if (this.isLocked() || options.length <= 1) { return; }
    options.splice(index, 1);
    this.setState({ question: { ...this.state.question, options } });
  }

  clear = () => {
    this.setState({
      question: this.normalize(this.props.question),
      lockSubmit: false,
    });
  }

  submit = () => {
    if (this.isLocked() || this.state.lockSubmit) { return; }
    const { question } = this.state;
    const isChoice = !!question.questionType && question.questionType !== 'TEXT';
    const options = question.options.map((o) => o.trim()).filter(Boolean);

    if (isChoice && options.length < 2) {
      return notify({
        type: 'error',
        title: 'Not enough options',
        message: 'A choice question needs at least two non-empty options.',
        duration: 3500,
      });
    }

    this.setState({ lockSubmit: true });

    const payload: any = {
      name: question.name,
      description: question.description,
      questionType: question.questionType,
      options: isChoice ? options.join(',') : '',
    };
    if (question.id) { payload.id = question.id; }

    this.questionService.add(
      payload,
      (response) => {
        if (!response.data.createQuestion && response.errors) {
          return notify({
            type: 'error',
            title: response.errors[0].message,
            callBack: () => this.setState({ lockSubmit: false }),
            duration: 3500,
          });
        }

        const errors = response.data.createQuestion.errors;
        if (!errors || !errors.length) {
          notify({
            type: 'success',
            title: this.isEdit() ? 'Question Updated' : 'Question Added',
            callBack: () => {
              if (this.isEdit()) {
                this.setState({ lockSubmit: false });
              } else {
                this.clear();
              }
            },
          });
        } else {
          notify({
            type: 'error',
            title: errors[0].field.toUpperCase(),
            message: errors[0].messages[0],
            callBack: () => this.setState({ lockSubmit: false }),
            duration: 3500,
          });
        }
      },
      (e) => e,
    );
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Question', link: '/dashboard/question' },
      this.isEdit()
        ? { text: 'Edit Question', link: `/dashboard/question/${this.props.question.id}` }
        : { text: 'Add Question', link: '/dashboard/question/add' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  typePicker = () => {
    const { question } = this.state;
    const locked = this.isLocked();
    return (
      <div className='cs-form__roles' role='radiogroup' aria-label='Question type'>
        {AddQuestion.TYPES.map((t) => {
          const selected = question.questionType === t.type;
          return (
            <button
              key={t.type}
              type='button'
              role='radio'
              aria-checked={selected}
              disabled={locked}
              className={'cs-form__role' + (selected ? ' cs-form__role--selected' : '')}
              onClick={() => this.onChange('questionType', t.type)}
            >
              <span className='cs-form__role-name'>
                {t.type}
                {selected && <span aria-hidden='true'>✓</span>}
              </span>
              <span className='cs-form__role-blurb'>{t.blurb}</span>
            </button>
          );
        })}
      </div>
    );
  }

  optionsEditor = () => {
    const { question } = this.state;
    const locked = this.isLocked();
    return (
      <section className='cs-form__section'>
        <h4 className='cs-form__legend'>Options</h4>
        <p className='cs-form__hint'>
          The answers the caller can choose from.
        </p>
        <div className='cs-form__opts'>
          {question.options.map((option, index) => (
            <div className='cs-form__opt' key={index}>
              <span className='cs-form__opt-letter' aria-hidden='true'>
                {String.fromCharCode(65 + index)}
              </span>
              <div className='cs-form__opt-field'>
                <Input
                  value={option}
                  placeholder={`Option ${index + 1}`}
                  disabled={locked}
                  onChange={(e) => this.optionChange(e.target.value, index)}
                />
              </div>
              <button
                type='button'
                className='cs-form__opt-remove'
                aria-label={`Remove option ${index + 1}`}
                disabled={locked || question.options.length <= 1}
                onClick={() => this.removeOption(index)}
              >
                ×
              </button>
            </div>
          ))}
          {!locked && question.options.length < AddQuestion.MAX_OPTIONS && (
            <button type='button' className='cs-form__add-opt' onClick={this.addOption}>
              + Add option
            </button>
          )}
          {!locked && (
            <p className='cs-form__note'>
              Up to {AddQuestion.MAX_OPTIONS} options; the completion email cannot report
              on more. Commas are not allowed inside an option.
            </p>
          )}
        </div>
      </section>
    );
  }

  render() {
    const { question, lockSubmit } = this.state;
    const isEdit = this.isEdit();
    const locked = this.isLocked();
    const isChoice = !!question.questionType && question.questionType !== 'TEXT';
    return (
      <div className='cs-form'>
        <Page
          title={isEdit ? 'Edit Question' : 'Add Question'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard/question'}>
              <MdArrowBack />
            </Link>
          }
        >
          <div className='cs-form__card'>
            <Card>
              {locked && (
                <div className='cs-form__lock' role='status'>
                  <b>In use — read only.</b>
                  <span>
                    This question is referenced by one or more questionnaires, so its
                    wording and options are locked to keep recorded answers meaningful.
                  </span>
                </div>
              )}

              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Question</h4>
                <p className='cs-form__hint'>
                  How the question is asked, and how the caller answers it.
                </p>
                {this.typePicker()}
                <div className='cs-form__grid'>
                  <div>
                    <Input
                      required
                      label='Name'
                      value={question.name}
                      disabled={locked}
                      onChange={(e) => this.onChange('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <Input
                      required
                      label='Description'
                      value={question.description}
                      disabled={locked}
                      onChange={(e) => this.onChange('description', e.target.value)}
                    />
                  </div>
                </div>
              </section>

              {isChoice && this.optionsEditor()}

              {!locked && (
                <div className='cs-form__actions'>
                  <Button
                    type='secondary'
                    onClick={this.clear}
                  >
                    {isEdit ? 'Reset' : 'Clear'}
                  </Button>
                  <Button
                    type='primary'
                    onClick={this.submit}
                    disabled={lockSubmit}
                  >
                    {lockSubmit ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Question'}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </Page>
      </div>
    );
  }
}

interface QuestionFormValue {
  id?: string;
  name: string;
  description: string;
  questionType: string;
  options: string[];
}

interface State {
  question: QuestionFormValue;
  lockSubmit: boolean;
}

interface Props {
  question: any;
}
